/** The identity of a federated (JWT-bearer) agentic client.
 *
 * P0 issues a subject for each federated client. The subject contains the
 * identity provider that approved the client. If you register the same
 * workload name with two providers, P0 makes two different clients.
 */
export type FederatedSubject = {
  /** The identity that the client shows to the gateway.
   *
   * This is the verified email if the token has one. If the token does not
   * have a verified email, this is the JWT `sub`. For example, GCP service
   * accounts have unique emails, but their subs are only numbers.
   *
   * This name can contain slashes. For example, a GitHub Actions `sub` can be
   * `repo:org/repo:ref:refs/heads/main`. You must percent-encode the subject
   * before you use it as a datastore document ID.
   */
  name: string;
  /** The ID of the `identity-provider` item that approved the client */
  providerId: string;
};

/** Makes the P0 subject for a federated agentic client. */
export const renderFederatedSubject = ({
  name,
  providerId,
}: FederatedSubject): string =>
  // Note that the issuer trust domain already includes the P0 host and tenant,
  // so this is globally unique.
  `federated/${providerId}/subject/${name}`;

const SUBJECT_PATTERN = /^federated\/([^/]+)\/subject\/(.+)$/;

/** Reads a P0 federated subject.
 *
 * Returns `undefined` if `subject` is not a federated subject. Client IDs are
 * not all federated subjects, because confidential clients use a different
 * format. A caller that handles the two types must accept `undefined`.
 */
export const parseFederatedSubject = (
  subject: string
): FederatedSubject | undefined => {
  const match = SUBJECT_PATTERN.exec(subject);
  if (!match) return undefined;
  const [, providerId, name] = match;
  // The pattern makes sure that these three parts exist. This test only
  // changes their type to `string`.
  if (!providerId || !name) return undefined;
  return { name, providerId };
};
